import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Shield, ArrowRight, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_empregos-ro/artifacts/8ffadfy5_c2ea4279-1513-4151-8935-c05559291e0c.png';

// Credenciais Admin
const ADMIN_EMAIL = 'admin@empregosro.com';
const ADMIN_PASSWORD = 'admin123';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [focusedField, setFocusedField] = useState(null);
  const [mounted, setMounted] = useState(false);

  // Efeito para animação de entrada suave
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!credentials.email || !credentials.password) {
      toast.error('Por favor, preencha todos os campos.', {
        position: 'top-center',
        style: { background: '#ef4444', color: 'white', border: 'none' }
      });
      return;
    }

    setLoading(true);
    
    // Simulação de delay para dar peso à interação
    setTimeout(() => {
      if (credentials.email === ADMIN_EMAIL && credentials.password === ADMIN_PASSWORD) {
        localStorage.setItem('admin_token', 'admin_authenticated');
        localStorage.setItem('admin_login_time', Date.now().toString());
        
        toast.success('Acesso autorizado!', {
          description: 'Redirecionando para o dashboard...',
          icon: <CheckCircle2 className="w-5 h-5 text-teal-400" />,
          position: 'top-center',
          style: { background: '#0f172a', color: 'white', border: '1px solid #134e4a' }
        });
        
        navigate('/admin/dashboard');
      } else {
        toast.error('Acesso negado', {
          description: 'Verifique suas credenciais.',
          position: 'top-center'
        });
        setLoading(false);
      }
    }, 800);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-teal-600/20 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]" />
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
      </div>

      <div className={`w-full max-w-md relative z-10 transition-all duration-1000 ease-out transform ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        
        {/* Logo Section */}
        <div className="text-center mb-8 relative group cursor-default">
          <div className="absolute inset-0 bg-teal-500/20 blur-3xl rounded-full opacity-0 group-hover:opacity-50 transition-opacity duration-700" />
          <img 
            src={LOGO_URL} 
            alt="Empregos RO" 
            className="h-20 mx-auto mb-6 relative drop-shadow-2xl transition-transform duration-500 hover:scale-105" 
          />
          <h1 className="text-3xl font-bold font-heading text-white tracking-tight">
            Portal Administrativo
          </h1>
          <p className="text-slate-400 mt-2 text-sm uppercase tracking-widest font-medium">
            Acesso Seguro
          </p>
        </div>

        {/* Card Section */}
        <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden group">
          {/* Top colored line */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-500 via-emerald-400 to-teal-500" />
          
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 shadow-inner group-hover:border-teal-500/30 transition-colors duration-500">
            <Shield className={`w-8 h-8 transition-all duration-500 ${loading ? 'text-teal-400 scale-110' : 'text-slate-400 group-hover:text-teal-500'}`} />
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className={`text-xs uppercase tracking-wide font-semibold transition-colors duration-300 ${focusedField === 'email' ? 'text-teal-400' : 'text-slate-400'}`}>
                Email Corporativo
              </Label>
              <div className={`relative group/input transition-all duration-300 rounded-xl ${focusedField === 'email' ? 'ring-2 ring-teal-500/20 bg-slate-800/80' : 'bg-slate-800/40 hover:bg-slate-800/60'}`}>
                <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${focusedField === 'email' ? 'text-teal-400' : 'text-slate-500'}`} />
                <Input
                  id="email"
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="admin@empregosro.com"
                  className="pl-12 h-12 bg-transparent border-none text-white placeholder:text-slate-600 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-xl"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className={`text-xs uppercase tracking-wide font-semibold transition-colors duration-300 ${focusedField === 'password' ? 'text-teal-400' : 'text-slate-400'}`}>
                Senha de Acesso
              </Label>
              <div className={`relative group/input transition-all duration-300 rounded-xl ${focusedField === 'password' ? 'ring-2 ring-teal-500/20 bg-slate-800/80' : 'bg-slate-800/40 hover:bg-slate-800/60'}`}>
                <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${focusedField === 'password' ? 'text-teal-400' : 'text-slate-500'}`} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="••••••••"
                  className="pl-12 pr-12 h-12 bg-transparent border-none text-white placeholder:text-slate-600 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white rounded-xl font-bold shadow-lg shadow-teal-900/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed group/btn mt-4"
            >
              <span className="flex items-center justify-center">
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3" />
                    Autenticando...
                  </>
                ) : (
                  <>
                    Acessar Painel
                    <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover/btn:translate-x-1" />
                  </>
                )}
              </span>
            </Button>
          </form>
        </div>
        
        {/* Footer info */}
        <p className="text-center text-slate-600 text-xs mt-8">
          &copy; {new Date().getFullYear()} Empregos RO. Sistema protegido por criptografia de ponta a ponta.
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;